import { API_SERVER, getShopifyAdminAppUrl } from "../services/CONSTANTS";

/**
 * Returns true when the app is being loaded standalone (a normal browser tab),
 * NOT embedded inside the Shopify Admin iframe.
 *
 * Real Shopify embedding always differs `window.top` from `window.self` (we're
 * inside an iframe), and Shopify always appends a `host` query param (and usually
 * `embedded=1`) to the iframe URL it loads. We treat the absence of both signals
 * as "standalone".
 *
 * Note: a browser tab already open with the pre-cutover JS bundle in memory before
 * this deploy can't be retroactively caught by this check -- only a fresh page load
 * (bookmark, new tab, or the axiosInstance 401 fallback redirect) runs this code.
 */
export const isRunningStandalone = () => {
  const params = new URLSearchParams(window.location.search);
  const hasEmbeddedParams = params.has("host") || params.get("embedded") === "1";
  const isTopFrame = window.top === window.self;
  return isTopFrame && !hasEmbeddedParams;
};

/**
 * Navigates to a Shopify-hosted URL (OAuth authorize screens, billing
 * confirmation pages, "https://{shop}/admin/apps/{id}" links, etc.), breaking
 * out of the Shopify Admin iframe first if we're embedded.
 *
 * Shopify's own admin/OAuth pages refuse to render inside ANY iframe (they
 * send `X-Frame-Options: deny`, and old-style "https://{shop}/admin/..." URLs
 * get redirected to the "https://admin.shopify.com/..." unified admin, which
 * has the same restriction). Doing a plain `window.location.href = url` while
 * embedded only navigates OUR iframe, so the browser blocks the destination
 * with an error like:
 *   "Refused to display 'https://admin.shopify.com/' in a frame because it
 *    set 'X-Frame-Options' to 'deny'."
 *
 * `window.open(url, "_top")` targets the top-level browsing context instead
 * of the current iframe, so the whole browser tab navigates away from the
 * embedded app -- exactly what Shopify's OAuth/admin pages require.
 */
export const redirectToShopifyUrl = (url) => {
  if (!url) return;
  if (window.top !== window.self) {
    window.open(url, "_top");
  } else {
    window.location.href = url;
  }
};

const EMBEDDED_SHOP_STORAGE_KEY = "embeddedShopDomain";
const SHOPIFY_INSTALL_QUERY_STORAGE_KEY = "shopifyInstallQuery";

/*
 * Raw query string from the iframe URL, without a leading "?".
 * Example: "embedded=1&hmac=abc&host=xyz&shop=acme.myshopify.com&timestamp=123"
 */
const currentQueryString = () =>
  (window.location.search || "").replace(/^\?/, "");

/*
 * Shopify signs the first iframe load with hmac (+ shop, timestamp, host, ...).
 * Client-side routing and App Bridge nav drop those params, and /install rejects
 * a shop-only URL with "Missing hmac parameter." Save the signed query on first
 * sight so reinstall can replay it.
 *
 * Example stored value: "embedded=1&hmac=abc&host=xyz&shop=acme.myshopify.com&timestamp=123"
 */
export const captureShopifyInstallQuery = () => {
  const search = currentQueryString();
  if (!search) {
    return;
  }
  const params = new URLSearchParams(search);
  if (params.get("hmac") && params.get("shop")) {
    sessionStorage.setItem(SHOPIFY_INSTALL_QUERY_STORAGE_KEY, search);
  }
};

/*
 * Signed query to send to /install. Prefer the first-load copy so later routes
 * that only keep ?shop= still have hmac.
 *
 * Example: "embedded=1&hmac=abc&shop=acme.myshopify.com&timestamp=123"
 */
export const getShopifyInstallQuery = () => {
  captureShopifyInstallQuery();
  const currentShop =
    new URLSearchParams(window.location.search).get("shop") ||
    window.shopify?.config?.shop ||
    sessionStorage.getItem(EMBEDDED_SHOP_STORAGE_KEY) ||
    "";
  const stored = sessionStorage.getItem(SHOPIFY_INSTALL_QUERY_STORAGE_KEY);
  if (stored) {
    const storedParams = new URLSearchParams(stored);
    const storedShop = storedParams.get("shop");
    if (
      storedParams.get("hmac") &&
      storedShop &&
      (!currentShop || storedShop === currentShop)
    ) {
      return stored;
    }
  }
  const search = currentQueryString();
  if (search && new URLSearchParams(search).get("hmac")) {
    return search;
  }
  return "";
};

/*
 * Resolves the myshopify.com domain for OAuth. Query params can disappear after
 * a client-side Redirect (e.g. /ui/ → /ui/connecting), so we also check App Bridge
 * and a sessionStorage copy from the first time we saw the shop.
 *
 * Example: "my-shop.myshopify.com"
 */
export const getEmbeddedShopDomain = () => {
  captureShopifyInstallQuery();
  const fromQuery = new URLSearchParams(window.location.search).get("shop");
  const fromAppBridge = window.shopify?.config?.shop;
  const fromStorage = sessionStorage.getItem(EMBEDDED_SHOP_STORAGE_KEY);
  const shop = fromQuery || fromAppBridge || fromStorage || "";

  if (shop) {
    sessionStorage.setItem(EMBEDDED_SHOP_STORAGE_KEY, shop);
  }

  return shop;
};

/**
 * Top-level-redirects out of the iframe to the backend's /install flow.
 * /install requires Shopify's hmac (not just shop) or it returns
 * "Missing hmac parameter."
 *
 * Used for shops that need to re-authorize (permanent token revoked) and for
 * brand-new shops that have never completed install.
 *
 * Shopify's OAuth screens refuse to render inside an iframe, so a normal same-frame
 * navigation would just fail silently (or throw the X-Frame-Options console error);
 * redirectToShopifyUrl breaks out to the top-level tab first.
 */
export const redirectToShopifyInstall = () => {
  const signedQuery = getShopifyInstallQuery();
  if (signedQuery) {
    redirectToShopifyUrl(`${API_SERVER}/install?${signedQuery}`);
    return;
  }

  const shop = getEmbeddedShopDomain();
  if (!shop) {
    console.error("Cannot start Shopify install: shop domain is missing");
    return;
  }

  /*
   * hmac was dropped (client nav) and we never captured it. Reload the app
   * inside Admin so Shopify issues a fresh signed iframe URL. Hitting
   * /install?shop=... without hmac is a 400.
   */
  const adminAppUrl = getShopifyAdminAppUrl(shop);
  if (adminAppUrl) {
    console.warn(
      "Cannot start Shopify install: hmac is missing. Reloading the app in Shopify Admin."
    );
    redirectToShopifyUrl(adminAppUrl);
    return;
  }

  console.error("Cannot start Shopify install: hmac and Shopify Admin app URL are missing");
};
